import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PublishIcon from "@mui/icons-material/Publish";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import DetailsPageHeader, {
  headerSecondaryButtonSx,
} from "../../../../../common/DetailsPageHeader";
import {
  GeneralAdminRoutes,
  SALES_SECTION_ORDER_STEP,
  mainLayoutStyles,
  showNotification,
  truncateString,
} from "../../../../../constants";
import { useAuth } from "../../../../../hooks";
import {
  deleteSalesPage,
  getSalesPages,
  getPublishedSalesPagesMap,
  isPublishPending,
  publishPage,
  savePageToStaging,
  toggleSalesPageEnabled,
} from "../../hooks/SalesPagesHook";
import MenuButtonModal from "../../components/MenuButtonModal/MenuButtonModal";

// A menu button is a simplified salesPages record (external link, no slug).
const isMenuButton = (page) => page.type === "menuButton" || (!!page.href && !page.slug);

/**
 * GeneralAdmin > QuickTales.com > Pages.
 * Lists the `salesPages` documents and lets the admin add / edit / delete
 * pages and jump into a page's section builder. See docs/SALES_PAGES_CMS.md
 */
const SalesPagesListContainer = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userId = currentUser?.id;

  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const [publishedPages, setPublishedPages] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editingButton, setEditingButton] = useState(null);

  const homeCount = pages.filter((p) => p.isHome).length;

  // Next order value (max + step) for a newly added record.
  const nextOrder =
    pages.reduce((max, p) => Math.max(max, p.order || 0), 0) +
    SALES_SECTION_ORDER_STEP;

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      setLoading(true);
      const [data, publishedMap] = await Promise.all([
        getSalesPages(),
        getPublishedSalesPagesMap(),
      ]);
      setPages(data);
      setPublishedPages(publishedMap);
    } catch (error) {
      console.log("Error loading pages:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (page) => {
    try {
      const next = !page.enabled;
      await toggleSalesPageEnabled(page, next, userId);
      // Reload so the single-enabled-home rule is reflected across rows.
      await loadPages();
    } catch (error) {
      console.log("Error toggling page:", error.message);
      showNotification("Error updating page");
      loadPages();
    }
  };

  const handleDelete = async (page) => {
    // Guarantee at least one home page: block deleting the only home page.
    if (page.isHome && homeCount <= 1) {
      showNotification("Cannot delete the only home page");
      return;
    }
    const confirmMsg = isMenuButton(page)
      ? `Delete "${page.title}"? This cannot be undone.`
      : `Delete "${page.title}" and all its sections? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      setLoading(true);
      await deleteSalesPage(page.id);
      showNotification("Page deleted");
      await loadPages();
    } catch (error) {
      console.log("Error deleting page:", error.message);
      showNotification("Error deleting page");
      setLoading(false);
    }
  };

  const handlePublishPage = async (page) => {
    try {
      setLoading(true);
      await publishPage(page.id, userId);
      showNotification("Page published");
      await loadPages();
    } catch (error) {
      console.log("Error publishing page:", error.message);
      showNotification("Error publishing page");
      setLoading(false);
    }
  };

  // Save = trigger the staging.quicktales.com redeploy for this page's drafts.
  const handleSavePage = async (page) => {
    try {
      setLoading(true);
      await savePageToStaging(page.id, userId);
      showNotification("Page saved — staging redeploying");
    } catch (error) {
      console.log("Error saving page:", error.message);
      showNotification("Error saving page");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => navigate(GeneralAdminRoutes.salesPageAdd);

  const handleAddMenuButton = () => {
    setEditingButton(null);
    setModalOpen(true);
  };

  const handleModalSaved = () => {
    setModalOpen(false);
    setEditingButton(null);
    loadPages();
  };

  const handleEditPage = (page) => {
    if (isMenuButton(page)) {
      setEditingButton(page);
      setModalOpen(true);
      return;
    }
    navigate(`/sales-pages/page/edit/${page.id}`);
  };

  const handleManageContent = (page) =>
    navigate(`/sales-pages/content/${page.id}`);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle="QuickTales.com — Pages"
        bgColor="#fff"
        hideBackArrow
        actions={
          <>
            <Button
              variant="outlined"
              sx={headerSecondaryButtonSx}
              onClick={handleAddMenuButton}
            >
              Add Menu Button
            </Button>
            <Button
              variant="outlined"
              sx={headerSecondaryButtonSx}
              onClick={handleAdd}
            >
              Add Page
            </Button>
          </>
        }
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid
          container
          direction="column"
          style={{ padding: 30, marginTop: 40 }}
        >
          <Paper sx={{ p: 3 }}>
            {pages.length === 0 && !loading ? (
              <Typography sx={emptyStyles}>
                No pages yet. Click "Add Page" to create one.
              </Typography>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellStyles}>Order</TableCell>
                    <TableCell sx={headCellStyles}>Title</TableCell>
                    <TableCell sx={headCellStyles}>Slug</TableCell>
                    <TableCell sx={headCellStyles}>Home</TableCell>
                    <TableCell sx={headCellStyles}>In Nav</TableCell>
                    <TableCell sx={headCellStyles}>Enabled</TableCell>
                    <TableCell sx={headCellStyles}>Status</TableCell>
                    <TableCell sx={headCellStyles} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pages.map((page) => {
                    const isOnlyHome = page.isHome && homeCount <= 1;
                    const menuButton = isMenuButton(page);
                    const pending = isPublishPending(
                      page,
                      publishedPages[page.id]
                    );
                    return (
                      <TableRow key={page.id} hover>
                        <TableCell sx={cellStyles}>{page.order}</TableCell>
                        <TableCell sx={cellStyles}>{page.title}</TableCell>
                        <TableCell sx={cellStyles}>
                          {menuButton
                            ? truncateString(page.href || "", 30)
                            : page.isHome
                            ? "/"
                            : `/${page.slug}`}
                        </TableCell>
                        <TableCell sx={cellStyles}>
                          {menuButton ? (
                            <Chip size="small" label="Menu Button" />
                          ) : page.isHome ? (
                            <Chip size="small" label="Home" />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell sx={cellStyles}>
                          {page.showInNav ? "Yes" : "No"}
                        </TableCell>
                        <TableCell sx={cellStyles}>
                          <Switch
                            checked={!!page.enabled}
                            onChange={() => handleToggle(page)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={cellStyles}>
                          {pending ? (
                            <Chip
                              size="small"
                              color="warning"
                              label={
                                publishedPages[page.id]
                                  ? "Unpublished changes"
                                  : "Not published"
                              }
                            />
                          ) : (
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              label="Published"
                            />
                          )}
                        </TableCell>
                        <TableCell sx={cellStyles} align="right">
                          {!menuButton ? (
                            <Tooltip title="Save to staging">
                              <IconButton
                                size="small"
                                onClick={() => handleSavePage(page)}
                              >
                                <SaveOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          <Tooltip
                            title={
                              pending
                                ? "Publish to live site"
                                : "Nothing to publish"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={!pending}
                                onClick={() => handlePublishPage(page)}
                                sx={{ color: pending ? "#2E7D32" : undefined }}
                              >
                                <PublishIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {!menuButton ? (
                            <Tooltip title="Manage Content">
                              <IconButton
                                size="small"
                                onClick={() => handleManageContent(page)}
                              >
                                <ArticleOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          <Tooltip title={menuButton ? "Edit" : "Edit Page"}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditPage(page)}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              isOnlyHome
                                ? "Cannot delete the only home page"
                                : menuButton
                                ? "Delete"
                                : "Delete Page"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={isOnlyHome}
                                onClick={() => handleDelete(page)}
                                sx={{ color: isOnlyHome ? undefined : "#B00020" }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>

      <MenuButtonModal
        open={modalOpen}
        menuButton={editingButton}
        nextOrder={nextOrder}
        onClose={() => setModalOpen(false)}
        onSaved={handleModalSaved}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default SalesPagesListContainer;

const headCellStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  color: "#020303",
};

const cellStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  color: "#020303",
};

const emptyStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  color: "#808080",
  p: 2,
};
